import clsx from "clsx";
import { ReactNode, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import {COMPONENT_TYPE} from "./generate-form/GenerateFormConfig";

type TFilterTree = {
  name: string;
  code: string;
  quantity?: number;
  icon?: ReactNode;
  disabled?: boolean;
};

type TTree = {
  code: string;
  name: string;
  filter?: TFilterTree[];
  icon?: ReactNode;
  quantity?: number;
};

type TSelectTree = {
  code?: string;
  name?: string;
  filter: TTree[];
  icon?: ReactNode;
  quantity?: number;
};

type TProps = {
  className?: string;
  codeCollapses: string[];
  handleChangeCollapsesCode: React.Dispatch<React.SetStateAction<string[]>>;
  idSelected: string;
  handleChangeSelectId: React.Dispatch<React.SetStateAction<string>>;
  selectTree: TSelectTree;
  onClickIcon?: boolean | null;
  getSelectedItem?: (data: any) => void;
  countStatus?: any;
  truncateTextNode?: boolean;
  expandAllNode?: boolean;
  defaultExpandCodes?: string[];
  onNodeExpand?: (node: any, expandCallbackFn?: () => void) => void;
};

const SelectTree = ({
  codeCollapses,
  handleChangeCollapsesCode,
  idSelected,
  handleChangeSelectId,
  selectTree,
  className,
  onClickIcon,
  getSelectedItem,
  countStatus,
  truncateTextNode = true,
  onNodeExpand,
  expandAllNode = true,
  defaultExpandCodes,
}: TProps) => {
  const handleCollapse = (code: string) => {
    if (codeCollapses.includes(code)) {
      handleChangeCollapsesCode(
        codeCollapses.filter((item: string) => item !== code)
      );
    } else {
      handleChangeCollapsesCode([...codeCollapses, code]);
    }
  };

  useEffect(() => {
    let newArr: any[] = [...defaultExpandCodes ?? [], selectTree.code];

    if (expandAllNode) {
      selectTree.filter.forEach(element => {
        newArr.push(element.code);
        if (element.filter) {
          newArr.push(...element.filter.map((item: any) => item.code));
        }
      });
	  }

    handleChangeCollapsesCode(newArr);
  }, [selectTree]);

  const handleSelected = (code: string): void => {
    if (idSelected === code) {
      handleChangeSelectId("");
    } else {
      handleChangeSelectId(code);
    }
  };

  return (
		<div className={`tree border border-left-0 ${className} p-2 position-relative`}>
			{selectTree.code && (
				<div
					onClick={() => handleCollapse(selectTree.code ?? '')}
					className="d-flex align-items-center ms-4 pb-2"
				>
					{!selectTree?.icon && (
						<span>
							{codeCollapses.includes(selectTree.code ?? '') ? (
								<i className="cursor-pointer bi bi-caret-down-fill color-blue-custom spaces pr-10"></i>
							) : (
								<i className="cursor-pointer bi bi-caret-right-fill color-blue-custom spaces pr-10"></i>
							)}
						</span>
					)}
					<span className="fw-bold d-flex truncate-text color-blue-custom">
						{selectTree?.icon && (
							<span className="spaces pr-10 d-flex align-items-center text-pri">
								{selectTree?.icon}
							</span>
						)}{' '}
						{selectTree.name}
					</span>
				</div>
			)}
			{(selectTree.code ? codeCollapses.includes(selectTree.code) : true) &&
				selectTree.filter?.map((item: any, index) => (
					<div key={item?.code + '-' + index}>
            {(item?.attributes?.renderType !== COMPONENT_TYPE.FILE && <div
              className={`${
                selectTree.code ? 'pl-13 spaces' : 'fw-bold'
              } d-flex row-tree fw-bold text-gray pb-2`}
              onClick={(e: any) => {
                const element = e.currentTarget as HTMLDivElement;

                const expanded = codeCollapses.includes(item.code);

                if (onNodeExpand && !expanded) {
                  element.setAttribute('data-kt-indicator', 'on');

                  onNodeExpand?.(item, () => {
                    handleCollapse(item.code);
                    element.setAttribute('data-kt-indicator', 'off');
                  });
                  return;
                }

                !onClickIcon && handleCollapse(item.code);
              }}
            >
							<span className="spaces pr-12 cursor-pointer">
								<i
                  className="bi bi-caret-right-fill text-dark d-inline-block"
                  style={{
                    transform: codeCollapses.includes(item.code)
                      ? 'rotate(90deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.25s ease',
                  }}
                  onClick={() => onClickIcon && handleCollapse(item.code)}
                ></i>
							</span>
              <span
                className="cursor-pointer truncate-text d-flex"
                onClick={() => handleChangeSelectId(item.code)}
              >
								{item?.icon && (
                  <span className="spaces pr-10 d-flex align-items-center">
										{item?.icon}
									</span>
                )}
                {item.name}
							</span>
              <span className="indicator-progress">
								<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
							</span>
            </div>)}
						{codeCollapses.includes(item.code) &&
							item?.filter?.map((i: any, index: any) => (
								<div
									key={i?.code + '-' + index}
									className={clsx(
										idSelected === i.code
											? `isFilterSelected ${
													item?.icon ? 'pl-38' : 'pl-30'
											  } spaces w-100 d-flex align-items-center pb-4`
											: `${
													item?.icon ? 'pl-38' : 'pl-30'
											  } spaces w-100 hover-row d-flex align-items-center cursor-pointer pb-4`,
										{
											'pe-none': i?.disabled,
										},
										{
											'truncate-text': truncateTextNode,
										}
									)}
									onClick={() => {
										getSelectedItem &&
											getSelectedItem(
												idSelected === i?.code
													? null
													: { ...i, parrentCode: item?.code }
											);
										handleSelected(i.code);
									}}
								>
									<span
										className={clsx('spaces pl-10 row-tree', {
											'truncate-text': truncateTextNode,
										})}
									>
										{i.name}
									</span>{' '}
									&nbsp;
									{i.quantity ? (
										<span className="quantity">{i.quantity}</span>
									) : (
										item?.icon && <span className="spaces W-10">{i?.icon}</span>
									)}
								</div>
							))}

            {(item?.attributes?.renderType === COMPONENT_TYPE.FILE &&
                <div
                    key={item?.code + '-' + index}
                    className={clsx(
                      idSelected === item.code
                        ? `isFilterSelected ${
                          item?.icon ? 'pl-38' : 'pl-30'
                        } spaces w-100 d-flex align-items-center pb-4`
                        : `${
                          item?.icon ? 'pl-38' : 'pl-30'
                        } spaces w-100 hover-row d-flex align-items-center cursor-pointer pb-4`,
                      {
                        'pe-none': item?.disabled,
                      },
                      {
                        'truncate-text': truncateTextNode,
                      }
                    )}
                    onClick={() => {
                      getSelectedItem &&
                      getSelectedItem(
                        idSelected === item?.code
                          ? null
                          : {...item, parrentCode: item?.code}
                      );
                      handleSelected(item.code);
                    }}
                >
									<span
                      className={clsx('spaces pl-10 row-tree', {
                        'truncate-text': truncateTextNode,
                      })}
                  >
										{item.name}
									</span>{' '}
                    &nbsp;
                  {item.quantity ? (
                    <span className="quantity">{item.quantity}</span>
                  ) : (
                    item?.icon && <span className="spaces W-10">{item?.icon}</span>
                  )}
                </div>)
            }
					</div>
				))}
			<div className="position-absolute bottom-0 px-2 left-14 w-89">
				<Row>
					{countStatus &&
						countStatus.map((item: any) => {
							return (
								<Col
									key={item?.code}
									xs={12}
									className={`d-flex flex-center ${item?.className} mt-5`}
								>
									{item?.name} : {item?.count}
								</Col>
							);
						})}
				</Row>
			</div>
		</div>
  );
};

export default SelectTree;
